<template>
  <div ref="root" class="hs-action-menu">
    <hs-button
      :icon="icon"
      :variant="variant"
      :size="size"
      :icon-only="iconOnly"
      @click="toggle"
    >
      <slot name="trigger">{{ label }}</slot>
    </hs-button>
    <teleport to="body">
      <transition name="hs-action-menu-pop">
        <div v-if="open" ref="menu" class="hs-action-menu__menu" :style="menuStyle">
          <button
            v-for="item in items"
            :key="item.value || item.label"
            type="button"
            class="hs-action-menu__item"
            :class="[`hs-action-menu__item--${item.tone || 'neutral'}`]"
            :disabled="item.disabled"
            @click="select(item)"
          >
            <hs-icon v-if="item.icon" :name="item.icon" :size="15" />
            <span>{{ item.label }}</span>
          </button>
          <slot />
        </div>
      </transition>
    </teleport>
  </div>
</template>

<script>
import HsButton from "./HsButton.vue";
import HsIcon from "./HsIcon.vue";

export default {
  name: "HsActionMenu",
  components: { HsButton, HsIcon },
  props: {
    items: { type: Array, default: () => [] },
    label: { type: String, default: "Actions" },
    icon: { type: String, default: "more-horizontal" },
    variant: { type: String, default: "secondary" },
    size: { type: String, default: "sm" },
    iconOnly: { type: Boolean, default: false },
    menuZIndex: { type: [String, Number], default: 5300 },
  },
  emits: ["select"],
  data() {
    return { open: false, menuStyle: {} };
  },
  mounted() {
    document.addEventListener("click", this.handleOutside);
    window.addEventListener("resize", this.updatePosition);
    window.addEventListener("scroll", this.updatePosition, true);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleOutside);
    window.removeEventListener("resize", this.updatePosition);
    window.removeEventListener("scroll", this.updatePosition, true);
  },
  methods: {
    toggle() {
      this.open = !this.open;
      if (this.open) this.$nextTick(this.updatePosition);
    },
    select(item) {
      if (item.disabled) return;
      this.$emit("select", item);
      this.open = false;
    },
    handleOutside(event) {
      if (!this.$refs.root?.contains(event.target) && !this.$refs.menu?.contains(event.target)) {
        this.open = false;
      }
    },
    updatePosition() {
      if (!this.open || !this.$refs.root) return;
      const rect = this.$refs.root.getBoundingClientRect();
      const width = 220;
      const left = Math.min(window.innerWidth - width - 12, rect.right - width);
      this.menuStyle = {
        left: `${Math.max(12, left)}px`,
        top: `${rect.bottom + 8}px`,
        width: `${width}px`,
        zIndex: String(this.menuZIndex),
      };
    },
  },
};
</script>

<style scoped>
.hs-action-menu {
  display: inline-flex;
}

.hs-action-menu__menu {
  background: var(--hs-surface, #fff);
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 16px;
  box-shadow: 0 22px 54px rgba(15, 23, 42, 0.18);
  display: grid;
  gap: 0.25rem;
  padding: 0.45rem;
  position: fixed;
}

.hs-action-menu__item {
  align-items: center;
  border-radius: 12px;
  color: var(--hs-text, #0f172a);
  display: flex;
  font-size: 0.84rem;
  font-weight: 850;
  gap: 0.55rem;
  min-height: 2.2rem;
  padding: 0 0.65rem;
  text-align: left;
}

.hs-action-menu__item:hover:not(:disabled) {
  background: color-mix(in srgb, var(--hs-primary, #3293ec) 10%, transparent);
  color: var(--hs-primary, #3293ec);
}

.hs-action-menu__item--danger {
  color: #dc2626;
}

.hs-action-menu__item:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.hs-action-menu-pop-enter-active,
.hs-action-menu-pop-leave-active {
  transition: opacity 0.16s ease, transform 0.16s ease;
}

.hs-action-menu-pop-enter-from,
.hs-action-menu-pop-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
